import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { getNotifications, markAllNotificationsRead, markNotificationRead } from '../api';
import type { AppNotification } from '../types';
import { useAuth } from './AuthContext';
import { useUi } from './UiContext';
import {
  isNative,
  notifyNative,
  prepareNativeNotifications,
  requestNativeNotificationPermission
} from '../native';

type NotificationPermission = 'unsupported' | 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied' | string;

interface NotificationsContextValue {
  items: AppNotification[];
  unreadCount: number;
  loading: boolean;
  permission: NotificationPermission;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  requestPermission: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { toast } = useUi();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>(isNative ? 'prompt' : 'unsupported');
  const lastSeqRef = useRef<number | null>(null);
  const storageKey = user ? `lspd_notif_last_seq_${user.id}` : '';

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const feed = await getNotifications(undefined, 30);
      setItems(feed.notifications);
      setUnreadCount(feed.unreadCount);
      if (lastSeqRef.current === null) {
        const stored = Number(localStorage.getItem(storageKey) || '0');
        lastSeqRef.current = stored > 0 ? stored : feed.lastSeq;
        localStorage.setItem(storageKey, String(lastSeqRef.current));
      }
    } catch {
      // Keep the last known state when the network is temporarily unavailable.
    } finally {
      setLoading(false);
    }
  }, [user, storageKey]);

  const pollNew = useCallback(async () => {
    if (!user || lastSeqRef.current === null) return;
    try {
      const feed = await getNotifications(lastSeqRef.current, 30);
      if (feed.notifications.length > 0) {
        setItems(previous => [...feed.notifications.slice().reverse(), ...previous].slice(0, 50));
        setUnreadCount(feed.unreadCount);
        for (const notification of feed.notifications) {
          void notifyNative(notification, feed.unreadCount);
        }
        const latest = feed.notifications[feed.notifications.length - 1]!;
        toast(feed.notifications.length === 1 ? `新通知：${latest.title}` : `收到 ${feed.notifications.length} 条新通知`, 'info');
      } else {
        setUnreadCount(feed.unreadCount);
      }
      lastSeqRef.current = feed.lastSeq;
      localStorage.setItem(storageKey, String(feed.lastSeq));
    } catch {
      // Network errors are handled by the offline banner / retry timer.
    }
  }, [user, storageKey, toast]);

  useEffect(() => {
    if (!user) {
      setItems([]);
      setUnreadCount(0);
      lastSeqRef.current = null;
      return;
    }
    const stored = Number(localStorage.getItem(storageKey) || '0');
    lastSeqRef.current = stored > 0 ? stored : null;
    void refresh();

    const tick = () => {
      if (document.visibilityState === 'visible') void pollNew();
    };
    const interval = window.setInterval(tick, 30_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
        void pollNew();
      }
    };
    const onResume = () => {
      void refresh();
      void pollNew();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('lspd:resume', onResume);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('lspd:resume', onResume);
    };
  }, [user, storageKey, refresh, pollNew]);

  useEffect(() => {
    if (!user || !isNative) return;
    let cancelled = false;
    void (async () => {
      const current = await prepareNativeNotifications();
      if (!cancelled) setPermission(current);
      if (current === 'prompt' || current === 'prompt-with-rationale') {
        const next = await requestNativeNotificationPermission();
        if (!cancelled) setPermission(next);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const markRead = useCallback(async (id: string) => {
    try {
      const result = await markNotificationRead(id);
      setItems(previous => previous.map(item => item.id === id ? { ...item, readAt: item.readAt ?? Date.now() } : item));
      setUnreadCount(result.unreadCount);
    } catch {
      toast('标记通知失败，请稍后重试', 'error');
    }
  }, [toast]);

  const markAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      const now = Date.now();
      setItems(previous => previous.map(item => ({ ...item, readAt: item.readAt ?? now })));
      setUnreadCount(0);
      toast('已全部标记为已读', 'success');
    } catch {
      toast('操作失败，请稍后重试', 'error');
    }
  }, [toast]);

  const requestPermission = useCallback(async () => {
    if (!isNative) return;
    const next = await requestNativeNotificationPermission();
    setPermission(next);
    if (next === 'granted') toast('系统通知已开启', 'success');
    else toast('通知权限未开启，可在系统设置中允许', 'error');
  }, [toast]);

  const value = useMemo(() => ({
    items,
    unreadCount,
    loading,
    permission,
    refresh,
    markRead,
    markAllRead,
    requestPermission
  }), [items, unreadCount, loading, permission, refresh, markRead, markAllRead, requestPermission]);

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  const value = useContext(NotificationsContext);
  if (!value) throw new Error('useNotifications must be used inside NotificationsProvider');
  return value;
}
